import { PromptMode, StyleMode } from '@loc/server/interface/task'
import { isNil, omitBy } from 'lodash-es'

import { MUSIC_SUPPORTS_AUDIO_WEIGHT } from '../../../_config/advancedSettings'
import removeEmptyValues from '../_helpers/removeEmptyValues'

import type {
  CommonTaskGenerationConfig,
  PromptMode as PromptModeType,
} from '@loc/server/interface/task'
import type { MusicGeneratorFormValuesNew } from '../../../_types/form'

interface BuildMusicGeneratePayloadState {
  lyrics: string
  customLyrics: string
  finalTaskType: MusicGeneratorFormValuesNew['taskType']
  finalImageUrl: string
  finalPromptSelectMode: PromptModeType
  finalPrompt: string
}

interface BuildMusicGeneratePayloadProps {
  values: MusicGeneratorFormValuesNew
  state: BuildMusicGeneratePayloadState
}

function getPromptStyleLabels(values: MusicGeneratorFormValuesNew) {
  if (values.promptTabsMode === 'smart') {
    return {
      labelsSummary: values.labelsSummary,
      description: values.recommendDescription || values.description,
    }
  }

  return {
    labelsSummary: values.SonglabelsSummary,
    description:
      values.SongrecommendDescription ||
      values.Songdescription ||
      values.Instrumentaldescription,
  }
}

function assertDefined<T>(
  value: T,
  message: string,
): asserts value is NonNullable<T> {
  if (value == null) {
    throw new Error(message)
  }
}

function getMusicAdvanced(advanced: MusicGeneratorFormValuesNew['advanced']) {
  if (!advanced) {
    return undefined
  }

  const { audioWeight, ...restValue } = advanced

  return omitBy(
    {
      ...restValue,
      audioWeight: MUSIC_SUPPORTS_AUDIO_WEIGHT
        ? advanced.audioWeight
        : undefined,
    },
    isNil,
  )
}

export function buildMusicGeneratePayload(
  props: BuildMusicGeneratePayloadProps,
): CommonTaskGenerationConfig | null {
  const { values, state } = props
  const sanitizedValues = removeEmptyValues({
    ...values,
    songReference:
      values.songReference?.disabled !== true
        ? values.songReference?.token
        : '',
    vocalReference:
      values.vocalReference?.disabled !== true
        ? values.vocalReference?.token
        : '',
    instrumentalReference:
      values.instrumentalReference?.disabled !== true
        ? values.instrumentalReference?.token
        : '',
  })
  const { labelsSummary, description } = getPromptStyleLabels(values)

  const {
    promptTabsMode,
    styleMode,
    customTaskType,
    songReference,
    vocalReference,
    instrumentalReference,
    advanced,
  } = sanitizedValues

  if (promptTabsMode === 'smart') {
    if (state.finalPromptSelectMode === PromptMode.QUICK) {
      return {
        taskType: state.finalTaskType,
        promptMode: state.finalPromptSelectMode,
        styleMode: StyleMode.PROMPT,
        prompt: state.finalPrompt,
        imageUrl: state.finalImageUrl,
      }
    }

    assertDefined(description, 'Prompt description is required')

    if (state.finalTaskType === 'Song') {
      return {
        taskType: state.finalTaskType,
        promptMode: PromptMode.SMART,
        prompt: state.finalPrompt,
        imageUrl: state.finalImageUrl,
        lyrics: state.lyrics,
        styleMode: StyleMode.PROMPT,
        labels: {
          description,
        },
        labelsSummary,
      }
    }

    return {
      taskType: state.finalTaskType,
      promptMode: PromptMode.SMART,
      prompt: state.finalPrompt,
      imageUrl: state.finalImageUrl,
      styleMode: StyleMode.PROMPT,
      labels: {
        description,
      },
      labelsSummary,
    }
  }

  if (customTaskType === 'Song') {
    if (styleMode === 'prompt' || styleMode === 'recommend') {
      assertDefined(description, 'Prompt description is required')

      return {
        taskType: customTaskType,
        styleMode: StyleMode.PROMPT,
        promptMode: PromptMode.CUSTOM,
        lyrics: state.customLyrics,
        labelsSummary,
        advanced: getMusicAdvanced(advanced),
        labels: {
          description,
        },
      }
    }

    if (!songReference && !vocalReference) {
      throw new Error('At least one song reference is required')
    }

    return {
      taskType: customTaskType,
      styleMode: StyleMode.REFERENCE,
      promptMode: PromptMode.CUSTOM,
      lyrics: state.customLyrics,
      songReference,
      vocalReference,
    }
  }

  if (styleMode === 'prompt') {
    assertDefined(description, 'Prompt description is required')

    return {
      taskType: customTaskType,
      styleMode: StyleMode.PROMPT,
      promptMode: PromptMode.CUSTOM,
      labelsSummary,
      advanced: getMusicAdvanced(advanced),
      labels: {
        description,
      },
    }
  }

  assertDefined(instrumentalReference, 'Instrumental reference is required')

  return {
    taskType: customTaskType,
    styleMode: StyleMode.REFERENCE,
    promptMode: PromptMode.CUSTOM,
    instrumentalReference,
  }
}
